import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, Whisper, IconButton, Tooltip, Checkbox, toaster, Message, Divider, SortType } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { EncyclopediaRelation, EncyclopediaRelationPaginationFilterRequest} from '@/models/encyclopedia-relation.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import EncyclopediaRelationDrawer from './encyclopedia-relation-drawer';
import EditIcon from '@rsuite/icons/Edit';
import { deleteEncyclopediaRelation, getPagedEncyclopediaRelations } from '@/services/encyclopedia-relation.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getEncyclopediaItemsLookup } from '@/services/encyclopedia-item.service';
import { getStoryChaptersLookup } from '@/services/story-chapter.service';
import { Icon } from '@rsuite/icons';
import { MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface EncyclopediaRelationListProps{
  storyId?: number,
  storyChapterId?: number,
  encyclopediaItemId?: number,
};

const EncyclopediaRelationList : React.FC<EncyclopediaRelationListProps> = ({storyId, storyChapterId, encyclopediaItemId}) => {
  const [filterReq, setFilterReq] = useState<EncyclopediaRelationPaginationFilterRequest>(new EncyclopediaRelationPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());  
  const [items, setItems] = useState<EncyclopediaRelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [idToEdit, setIdToEdit] = useState<number|undefined>(undefined);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: EncyclopediaRelationPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        storyId: storyId ?? filterReq.storyId,
        storyChapterId: storyChapterId ?? filterReq.storyChapterId,
        encyclopediaItemId: encyclopediaItemId ?? filterReq.encyclopediaItemId
      };
      const result: PagedResponseModel<EncyclopediaRelation> = await getPagedEncyclopediaRelations(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching links:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIdToEdit(undefined);
    fetchData(filterReq);
  };
  
  const onEditItem = (id: number)=>{
    setIdToEdit(id);
    handleOpenDrawer();
  };
  const onDeleteItem = (id: number, title?: string) => {

    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteEncyclopediaRelation(id);
        if (result.success) {
          toastSuccessMessage(`Relation Successfully Removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting Relation:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear(); 
      }
    };

    const handleNoClick = () => {
      toaster.clear(); 
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{marginBottom:10}}>Are you sure you want to remove <strong>{title ?? 'Relation'}</strong>?</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };


  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <Button appearance="primary" onClick={handleOpenDrawer}>
            New Relation
          </Button>
          {storyChapterId && <Button appearance="primary" onClick={() => nav(`/encyclopedia-items/new`, {state:{storyChapterId}})}>
            New Encyclopedia Item
          </Button>}
        </Stack>
        <Stack spacing={6}>
          {!encyclopediaItemId && <FetchingSelect
            label="Encyclopedia Item"
            fetchData={(text, page) => getEncyclopediaItemsLookup(text, page)}
            value={filterReq.encyclopediaItemId}
            onChange={dataKey => setFilterReq(pre => ({...pre, encyclopediaItemId: dataKey}))}
          />}    
          {!storyChapterId && <FetchingSelect
            label="Story Chapter"
            fetchData={(text, page) => getStoryChaptersLookup(text, page, storyId)}
            value={filterReq.storyChapterId}
            onChange={dataKey => setFilterReq(pre => ({...pre, storyChapterId: dataKey}))}
          />}    
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, onlyUnlockAtChapterStart: checked == true ? true : undefined}))} >Unlock At Chapter Start</Checkbox>
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, onlyUnlockAtChapterEnd: checked == true ? true : undefined}))} >Unlock At Chapter End</Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        {!encyclopediaItemId && <Column flexGrow={2} align="left" >
          <HeaderCell>Encyclopedia Item</HeaderCell>
          <Cell dataKey='encyclopediaItem.name' style={{ padding: 5 }}>
            {(rowData: EncyclopediaRelation) => <Button onClick={() => nav(`/encyclopedia-items/${rowData.encyclopediaItemId}`)} appearance="link">{rowData.encyclopediaItem.name}</Button>}
          </Cell>
        </Column>}
        {!storyChapterId && <Column flexGrow={2} align="left" >
          <HeaderCell>Story Chapter</HeaderCell>
          <Cell dataKey='storyChapter.name' style={{ padding: 5 }}>
            {(rowData: EncyclopediaRelation) => <Button onClick={() => nav(`/stories/chapters/${rowData.storyChapterId}`)} appearance="link">{rowData.storyChapter.name}</Button>}
          </Cell>          
        </Column>}
        <Column flexGrow={1} align="center" >
          <HeaderCell>Unlock At Chapter Start</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: EncyclopediaRelation) => (   
            <Checkbox checked={rowData.unlockAtChapterStart} readOnly></Checkbox>
          )}
          </Cell>
        </Column> 

        <Column flexGrow={1} fixed="right" verticalAlign="middle" >
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: EncyclopediaRelation) => 
            <Stack spacing={16}>
              <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit Encyclopedia Relation</Tooltip>}>
                <IconButton onClick={()=>onEditItem(rowData.id)} icon={<EditIcon />} circle size="sm" />
              </Whisper>
              <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete Encyclopedia Relation</Tooltip>}>
                <IconButton circle size="sm" color="red" appearance="ghost"
                  onClick={()=> onDeleteItem(
                    rowData.id, 
                    storyChapterId ? rowData.encyclopediaItem.name : (encyclopediaItemId ? rowData.storyChapter.name : undefined)
                  )} 
                  icon={<Icon as={MdDelete}/>} />
              </Whisper>
            </Stack>}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

      <EncyclopediaRelationDrawer 
        relationId={idToEdit}
        storyId={storyId} 
        storyChapterId={storyChapterId} 
        encyclopediaItemId={encyclopediaItemId}
        open={openDrawer} 
        onClose={handleCloseDrawer} 
      />
    </PageContent>
  );
};

export default EncyclopediaRelationList;
